import { Fragment, ReactNode, useState, useRef, useEffect } from 'react';
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react';
import { KeyHint } from '@/components/KeyHint';
import { cn } from '@/lib/utils';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItemBase,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Button } from '../ui';
import { Breadcrumb } from './Breadcrumb';

export interface PageTitleAction {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'danger';
    testId?: string;
}

export interface PageTitleBreadcrumb {
    label: string;
    icon?: React.ReactNode;
    onClick?: () => void;
}

interface PageTitleProps {
    icon?: React.ReactNode;
    text: string;
    titleAccessory?: ReactNode;
    breadcrumbs?: PageTitleBreadcrumb[];
    description?: string;
    actions?: PageTitleAction[];
    menuContent?: (close: () => void) => ReactNode;
    menuContentClassName?: string;
    onRename?: (newName: string) => void;
    allowRename?: boolean;
    className?: string;
    actionsTriggerTestId?: string;
}

export function PageTitle({
    icon,
    text,
    titleAccessory,
    breadcrumbs,
    description,
    actions,
    menuContent,
    menuContentClassName,
    onRename,
    allowRename = true,
    className,
    actionsTriggerTestId,
}: PageTitleProps) {
    const hasBreadcrumbs = breadcrumbs !== undefined && breadcrumbs.length > 0;
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(text);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [menuAlignOffset, setMenuAlignOffset] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const menuContentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!dropdownOpen) return;
        const handleScroll = (event: Event) => {
            if (
                event.target instanceof Node &&
                menuContentRef.current?.contains(event.target)
            ) {
                return;
            }
            setDropdownOpen(false);
        };
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [dropdownOpen]);

    // Open the menu at the horizontal click position rather than at the
    // trigger's left edge: capture the click offset and feed it to alignOffset.
    const handleTriggerPointerDown = (
        event: React.PointerEvent<HTMLButtonElement>,
    ) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuAlignOffset(event.clientX - rect.left);
    };

    // Keyboard opens have no pointer position: align to the start. Reset only
    // while closed so the exit animation never repositions (which would flash).
    const handleTriggerKeyDown = () => {
        if (!dropdownOpen) setMenuAlignOffset(0);
    };

    const hasActions = actions !== undefined && actions.length > 0;
    const hasMenuContent = menuContent !== undefined;
    const hasMenu = hasActions || hasMenuContent;
    const canRename = onRename !== undefined && allowRename;
    const menuAsWholeRowTrigger = hasMenu && !canRename && !isEditing;

    const renderedTitleAccessory = titleAccessory ? (
        <span className="shrink-0 text-2xl leading-9">
            <span className="inline-flex align-middle">{titleAccessory}</span>
        </span>
    ) : null;

    useEffect(() => {
        setEditValue(text);
    }, [text]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const commitRename = () => {
        const trimmed = editValue.trim();
        if (trimmed && trimmed !== text) {
            onRename?.(trimmed);
        }
        setIsEditing(false);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            commitRename();
        }
        if (event.key === 'Escape') {
            setEditValue(text);
            setIsEditing(false);
            inputRef.current?.blur();
        }
    };

    const actionsMenu = hasMenu && !isEditing && (
        <DropdownMenu
            open={dropdownOpen}
            onOpenChange={setDropdownOpen}
            modal={false}
        >
            {menuAsWholeRowTrigger ? (
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        className={cn(
                            'isolate box-border flex min-h-9 w-fit max-w-full min-w-0 flex-row items-center gap-2 rounded-[5px]',
                            'px-4 -mx-4 py-0 text-left outline-none transition-colors',
                            'hover:bg-grey-600 data-[state=open]:bg-grey-600',
                        )}
                        aria-haspopup="menu"
                        aria-expanded={dropdownOpen}
                        data-testid={actionsTriggerTestId}
                        onPointerDown={handleTriggerPointerDown}
                        onKeyDown={handleTriggerKeyDown}
                    >
                        <div className="flex min-w-0 max-w-full flex-row items-center gap-1.5">
                            {icon && (
                                <span className="size-6 shrink-0 flex items-center justify-center text-grey-200 [&>svg]:size-6">
                                    {icon}
                                </span>
                            )}
                            <h1
                                role="heading"
                                aria-level={1}
                                className="m-0 flex min-h-9 min-w-0 max-w-full items-center text-2xl font-medium leading-9 tracking-[-0.01em] text-white-100"
                            >
                                <span className="min-w-0 truncate">{text}</span>
                            </h1>
                            {renderedTitleAccessory}
                        </div>
                        {dropdownOpen ? (
                            <CaretUpIcon className="size-4 shrink-0 text-white-100" />
                        ) : (
                            <CaretDownIcon className="size-4 shrink-0 text-white-100" />
                        )}
                    </button>
                </DropdownMenuTrigger>
            ) : (
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        size="small"
                        className="shrink-0 size-9 flex items-center justify-end text-white-100 rounded-[5px] outline-none hover:bg-transparent"
                        aria-haspopup="menu"
                        aria-expanded={dropdownOpen}
                        data-testid={actionsTriggerTestId}
                    >
                        {dropdownOpen ? (
                            <CaretUpIcon className="size-4" />
                        ) : (
                            <CaretDownIcon className="size-4" />
                        )}
                    </Button>
                </DropdownMenuTrigger>
            )}
            <DropdownMenuContent
                ref={menuContentRef}
                side="bottom"
                align={menuAsWholeRowTrigger ? 'start' : 'end'}
                alignOffset={menuAsWholeRowTrigger ? menuAlignOffset : 0}
                sideOffset={8}
                className={cn('min-w-[200px]', menuContentClassName)}
            >
                {hasMenuContent ? (
                    menuContent!(() => setDropdownOpen(false))
                ) : (
                    <DropdownMenuGroup>
                        {actions!.map((action) => (
                            <DropdownMenuItemBase
                                key={action.label}
                                onClick={() => {
                                    action.onClick();
                                    setDropdownOpen(false);
                                }}
                                variant={action.variant ?? 'default'}
                                data-testid={action.testId}
                            >
                                <span className="text-sm font-medium">
                                    {action.label}
                                </span>
                            </DropdownMenuItemBase>
                        ))}
                    </DropdownMenuGroup>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );

    if (menuAsWholeRowTrigger) {
        return (
            <div
                className={cn(
                    'box-border flex min-w-0 max-w-full flex-row items-stretch',
                    className,
                )}
            >
                {actionsMenu}
            </div>
        );
    }

    return (
        <div className="flex flex-col">
            <div
                className={cn(
                    'flex h-9 w-fit max-w-full min-w-0 flex-row items-center box-border px-4 -mx-4',
                    hasBreadcrumbs ? 'gap-2' : 'gap-1',
                    hasMenu &&
                        !isEditing &&
                        'rounded-[5px] transition-colors hover:bg-grey-600',
                    isEditing && 'rounded-[5px] border border-grey-500',
                    className,
                )}
            >
                {hasBreadcrumbs &&
                    !isEditing &&
                    breadcrumbs.map((crumb, index) => (
                        <Fragment key={`${index}-${crumb.label}`}>
                            <Breadcrumb {...crumb} />
                            <span className="shrink-0 text-2xl font-medium leading-9 tracking-[-0.01em] text-grey-200">
                                /
                            </span>
                        </Fragment>
                    ))}
                <div
                    className={cn(
                        'flex flex-row items-center gap-1.5',
                        isEditing && 'min-w-0 flex-1',
                    )}
                >
                    {icon && (
                        <span className="size-6 shrink-0 flex items-center justify-center text-grey-200 [&>svg]:size-6">
                            {icon}
                        </span>
                    )}
                    {isEditing ? (
                        <h1 className="m-0 min-w-0 flex-1">
                            <div className="flex flex-row items-center gap-2 min-w-0 flex-1">
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={editValue}
                                    size={Math.max(editValue.length || 1, 8)}
                                    onChange={(event) =>
                                        setEditValue(event.target.value)
                                    }
                                    onKeyDown={handleKeyDown}
                                    onBlur={commitRename}
                                    className="min-w-0 max-w-full h-9 bg-transparent border-0 outline-none text-2xl font-medium leading-9 tracking-[-0.01em] text-white-100 placeholder:text-grey-400"
                                    aria-label="Rename"
                                />
                                <KeyHint theme="dark" className="shrink-0">
                                    ↵
                                </KeyHint>
                            </div>
                        </h1>
                    ) : canRename ? (
                        <h1 className="m-0 min-w-0 flex-1">
                            <button
                                type="button"
                                onClick={() => setIsEditing(true)}
                                className="flex w-full min-w-0 items-center min-h-9 text-2xl font-medium leading-9 tracking-[-0.01em] text-white-100 text-left cursor-pointer"
                            >
                                <span className="min-w-0 truncate">{text}</span>
                            </button>
                        </h1>
                    ) : (
                        <h1 className="m-0 flex min-h-9 min-w-0 items-center text-2xl font-medium leading-9 tracking-[-0.01em] text-white-100">
                            <span className="min-w-0 truncate">{text}</span>
                        </h1>
                    )}
                    {!isEditing && renderedTitleAccessory}
                </div>
                {actionsMenu}
            </div>
            {description && (
                <p className="mt-2 text-base font-normal leading-6 text-grey-200">
                    {description}
                </p>
            )}
        </div>
    );
}
